import { useEffect, useRef, useState } from 'react'
import { ActionType, type Chart, type Crosshair } from 'klinecharts'
import type { EventBus } from '../core/EventBus'
import type { SplitEvent } from '../types/splits'
import { DEFAULT_SESSION_TZ } from '../utils/sessionDay'
import { SPLIT_BADGE_COLOR } from '../utils/splitMarkersIndicator'
import { CHART_FONT_FAMILY } from '../utils/chartColors'
import { matchChartEvent } from './matchEarnings'

export interface SplitTooltipProps {
  chart: Chart | null
  eventBus: EventBus | null
  /** Split events currently rendered as badges (host-supplied). */
  splits: SplitEvent[] | undefined
  /** Session timezone used to bucket a hovered bar into its trading day. */
  timezone: string
}

/** Full-word split type for the hover card ("R/S" stays for the compact label). */
function splitTypeLabel(ev: SplitEvent): string {
  switch (ev.adjustmentType) {
    case 'reverse_split':
      return 'Reverse split'
    case 'forward_split':
      return 'Split'
    case 'stock_dividend':
      return 'Stock dividend'
    default:
      return 'Split'
  }
}

/**
 * Hover card for a split marker. The badge itself is a bare outlined "S" (no
 * inline ratio - it clutters the chart), so this thin React layer supplies the
 * detail on hover: split type, ratio, and date. Same crosshair-subscription
 * pattern as `EarningsTooltip`; renders nothing when no split badge is under
 * the crosshair.
 */
export function SplitTooltip({ chart, eventBus, splits, timezone }: SplitTooltipProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<{ x: number; ev: SplitEvent; width: number } | null>(null)

  useEffect(() => {
    if (!chart) return

    const tz = timezone || DEFAULT_SESSION_TZ
    const onCrosshair = (crosshair: Crosshair) => {
      const bar = crosshair.kLineData
      const x = crosshair.x ?? crosshair.realX
      const idx = crosshair.dataIndex ?? crosshair.realDataIndex
      if (!bar || x == null || idx == null) {
        setHover(null)
        return
      }
      const ev = matchChartEvent(splits, chart.getDataList(), idx, tz)
      setHover(ev ? { x, ev, width: wrapRef.current?.clientWidth ?? 0 } : null)
    }

    chart.subscribeAction(ActionType.OnCrosshairChange, onCrosshair)
    const unsubLeave = eventBus?.on('crosshair:leave', () => setHover(null))

    return () => {
      chart.unsubscribeAction(ActionType.OnCrosshairChange, onCrosshair)
      unsubLeave?.()
    }
  }, [chart, eventBus, splits, timezone])

  // Clamp the card within the container width (measured at the crosshair
  // event) so it never overflows the right edge. Width guess keeps the clamp
  // cheap without measuring the card.
  const CARD_W = 150
  let left = hover?.x ?? 0
  if (hover && hover.width > 0) {
    left = Math.min(Math.max(left, 8), Math.max(8, hover.width - CARD_W - 8))
  }

  const ev = hover?.ev
  const ratio =
    ev && ev.splitFrom != null && ev.splitTo != null ? `${ev.splitFrom}:${ev.splitTo}` : null

  return (
    <div
      ref={wrapRef}
      data-testid="split-tooltip-wrap"
      className="pointer-events-none absolute inset-0 z-20"
    >
      {ev && (
        <div
          data-testid="split-tooltip"
          className="absolute rounded-md border border-border bg-[hsl(var(--chart-canvas))] px-2.5 py-2 text-[11px] leading-tight text-[hsl(var(--chart-text))] shadow-xl shadow-black/40"
          style={{
            left,
            bottom: 28,
            width: CARD_W,
            fontFamily: CHART_FONT_FAMILY,
          }}
        >
          <div className="mb-1 flex items-center gap-1.5 font-semibold">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ background: SPLIT_BADGE_COLOR }}
            />
            {splitTypeLabel(ev)}
          </div>
          {ratio && (
            <div className="flex justify-between">
              <span className="text-[hsl(var(--chart-text-muted))]">Ratio</span>
              <span>{ratio}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-[hsl(var(--chart-text-muted))]">Date</span>
            <span>{ev.date}</span>
          </div>
        </div>
      )}
    </div>
  )
}
